import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProductBySlug } from '../../services/api';
import { useCart } from '../../context/CartContext';

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    getProductBySlug(slug)
      .then(data => {
        setProduct(data);
        setSelectedImage(data.image_url);
        if (window.fbq) {
          window.fbq('track', 'ViewContent', {
            content_name: data.name,
            content_ids: [data.slug],
            content_type: 'product',
            value: data.price,
            currency: 'ILS'
          });
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <p className="text-center py-20 text-neutral-400">جار التحميل...</p>;
  if (!product) return <p className="text-center py-20 text-neutral-400">المنتج غير موجود.</p>;

  // Parse specs
  let specs = {};
  if (product.specs) {
    if (typeof product.specs === 'string') {
      try { specs = JSON.parse(product.specs); } catch (e) { specs = {}; }
    } else {
      specs = product.specs;
    }
  }

  // Parse additional images
  let additionalImages = [];
  if (product.images) {
    if (typeof product.images === 'string') {
      try { additionalImages = JSON.parse(product.images); } catch (e) { additionalImages = []; }
    } else {
      additionalImages = product.images;
    }
  }

  const allImages = [product.image_url, ...additionalImages].filter(Boolean);
  const inStock = product.stock > 0;
  const hasDiscount = product.old_price && product.old_price > product.price;
  const discountPercent = hasDiscount ? Math.round((1 - product.price / product.old_price) * 100) : 0;

  return (
    <div className="min-h-screen bg-black py-12 px-4" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-neutral-400 mb-6">
          <Link to="/" className="hover:text-white">الرئيسية</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-white">المتجر</Link>
          <span>/</span>
          <span className="text-white">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Images Column */}
          <div>
            {/* Main Image */}
            <div className="aspect-square bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-800 mb-4">
              <img
                src={selectedImage || product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(img)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 shrink-0 transition ${
                      selectedImage === img ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-500'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Column */}
          <div className="text-right text-white">
            {/* Category & Brand */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs text-neutral-400 bg-neutral-900 px-3 py-1 rounded-full">
                {product.category_ar || product.category}
              </span>
              <span className="text-xs text-neutral-500">{product.brand}</span>
            </div>

            {/* Name */}
            <h1 className="text-3xl md:text-4xl font-black mb-4 leading-tight">{product.name}</h1>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-3xl font-black text-red-500">{product.price} شيكل</span>
              {product.old_price && (
                <>
                  <span className="text-xl text-neutral-500 line-through">{product.old_price} شيكل</span>
                  <span className="bg-red-600 text-white text-sm font-bold px-2 py-1 rounded">
                    وفر {discountPercent}%
                  </span>
                </>
              )}
            </div>

            {/* Stock Status */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold mb-6 ${
              inStock ? 'bg-green-900/30 text-green-400 border border-green-800' : 'bg-red-900/30 text-red-400 border border-red-800'
            }`}>
              <span className={`w-2 h-2 rounded-full ${inStock ? 'bg-green-400' : 'bg-red-400'}`}></span>
              {inStock ? 'متوفر في المخزون' : 'نفذت الكمية'}
            </div>

            {/* Description */}
            {product.description && (
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-3">الوصف</h3>
                <div className="text-neutral-300 leading-relaxed whitespace-pre-line text-sm">
                  {product.description}
                </div>
              </div>
            )}

            {/* Specs Table */}
            {Object.keys(specs).length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-3">المواصفات</h3>
                <div className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
                  <table className="w-full text-right">
                    <tbody>
                      {Object.entries(specs).map(([key, val], idx) => (
                        <tr key={key} className={`${idx % 2 === 0 ? 'bg-neutral-900' : 'bg-neutral-950'} border-b border-neutral-800 last:border-0`}>
                          <td className="py-3 px-4 text-neutral-400 font-medium w-1/3">{key}</td>
                          <td className="py-3 px-4 text-white">{val}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Add to Cart Button */}
            <button
              onClick={() => addToCart(product)}
              disabled={!inStock}
              className={`w-full py-5 font-bold text-lg rounded-xl transition-all ${
                inStock
                  ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20 hover:shadow-red-600/40'
                  : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
              }`}
            >
              {inStock ? 'أضف إلى السلة' : 'غير متوفر حالياً'}
            </button>

            {/* COD Notice */}
            {inStock && (
              <p className="text-center text-neutral-500 text-xs mt-3">
                الدفع عند الاستلام • توصيل مجاني للطلبات فوق 500 شيكل
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}